import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { PRODUCTS, CATEGORIES } from "../data/products";
import { ProductCard } from "../components/ProductCard";
import { usePageMeta } from "../hooks/usePageMeta";

export const Products = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialCat = searchParams.get("category") || "All";
    const [activeCategory, setActiveCategory] = useState(initialCat);
    const [query, setQuery] = useState("");

    usePageMeta({
        title:
            activeCategory && activeCategory !== "All"
                ? `${activeCategory} Research Peptides — Quantum Genesis Labs`
                : "Research Peptide Catalog — Quantum Genesis Labs",
        description:
            activeCategory && activeCategory !== "All"
                ? `Browse premium research-grade ${activeCategory.toLowerCase()} peptides from Quantum Genesis Labs. High-purity lyophilized formulations for laboratory research use only.`
                : "Browse the complete catalog of premium research-grade peptides — Weight Management, Recovery, Cognitive & Wellness, Longevity, Hormonal Support, Skin & Beauty, Blends and Essentials. For research use only.",
        path:
            activeCategory && activeCategory !== "All"
                ? `/products?category=${encodeURIComponent(activeCategory)}`
                : "/products",
    });

    useEffect(() => {
        const c = searchParams.get("category") || "All";
        setActiveCategory(c);
    }, [searchParams]);

    const handleSelectCategory = (c) => {
        setActiveCategory(c);
        if (c === "All") {
            searchParams.delete("category");
        } else {
            searchParams.set("category", c);
        }
        setSearchParams(searchParams, { replace: true });
    };

    const filtered = useMemo(() => {
        const matches = PRODUCTS.filter((p) => {
            const matchesCat = activeCategory === "All" || p.category === activeCategory;
            const matchesQuery = !query.trim() || p.name.toLowerCase().includes(query.toLowerCase());
            return matchesCat && matchesQuery;
        });

        // Sort alphabetically (A→Z) by product name within categories.
        // In the "All" view, pin Essentials (Bacteriostatic Water, Insulin
        // Syringes) to the very end so flagship research peptides lead.
        const isEssential = (p) => p.category === "Essentials";
        const byName = (a, b) => a.name.localeCompare(b.name);

        if (activeCategory === "All") {
            const research = matches.filter((p) => !isEssential(p)).sort(byName);
            const essentials = matches.filter(isEssential).sort(byName);
            return [...research, ...essentials];
        }
        return [...matches].sort(byName);
    }, [activeCategory, query]);

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16" data-testid="products-page">
            <header className="mb-10 max-w-3xl">
                <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-400 mb-3">Catalog</p>
                <h1 className="text-4xl md:text-5xl font-light tracking-[-0.03em]">
                    The complete <span className="text-gradient">research catalog</span>
                </h1>
                <p className="mt-5 text-slate-400 leading-relaxed">
                    Browse our complete library of premium research-grade peptides, cleanly organized by category.
                </p>
            </header>

            <div className="flex flex-col lg:flex-row gap-4 mb-10">
                <div className="relative lg:w-72">
                    <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search peptides..."
                        data-testid="product-search-input"
                        className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white/[0.03] border border-white/10 text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-400/40"
                    />
                </div>

                <div className="flex flex-wrap gap-2 flex-1">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => handleSelectCategory(c)}
                            data-testid={`category-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                                activeCategory === c
                                    ? "bg-indigo-500/15 border-indigo-400/40 text-white"
                                    : "bg-transparent border-white/10 text-slate-400 hover:text-white hover:border-white/25"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            {/* Promotional banner — buy more, save more */}
            <div
                data-testid="promo-buy-more-save-more"
                className="relative overflow-hidden rounded-2xl border border-indigo-500/30 mb-10 px-5 sm:px-8 py-5 sm:py-6"
                style={{
                    background: "linear-gradient(110deg, rgba(26,11,61,0.85) 0%, rgba(42,20,99,0.8) 35%, rgba(31,42,140,0.75) 70%, rgba(27,61,184,0.7) 100%)",
                }}
            >
                <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-300/45 to-transparent" />
                <span className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full bg-violet-500/20 blur-3xl" />
                <span className="pointer-events-none absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-indigo-500/20 blur-3xl" />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <span
                        className="inline-flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-white/10 border border-white/20 shadow-[0_0_18px_rgba(167,139,250,0.45)] text-xl"
                        aria-hidden="true"
                    >
                        🔥
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] tracking-[0.28em] uppercase text-violet-200/90 mb-1">
                            Buy More. Save More.
                        </p>
                        <p className="text-white text-base sm:text-lg leading-snug">
                            Spend <span className="font-semibold">$250 CAD</span> or more and enjoy{" "}
                            <span className="font-semibold text-white">10% OFF</span> automatically +{" "}
                            <span
                                className="font-semibold"
                                style={{
                                    color: "#FFD700",
                                    textShadow: "0 1px 2px rgba(0,0,0,0.45), 0 0 12px rgba(255, 215, 0, 0.35)",
                                }}
                            >
                                FREE Canada-wide shipping.
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-[#0A0A12]/60 p-16 text-center" data-testid="no-products-message">
                    <p className="text-slate-400">No products match your filters.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
                    {filtered.map((p) => (
                    <ProductCard key={p.id} product={p} />
                ))}
                </div>
            )}
        </div>
    );
};

export default Products;
